// The chance cards' moment of suspense: a coin in the air, a die tumbling, a spotlight
// running round the table, a wheel slowing down — and only then the result.
//
// A card's play() rolls through the injected randomInt like it always did. The controller
// records those rolls while the card resolves and asks gambleOutcome what they meant, so
// the cards themselves know nothing about the show. The answer rides on the play's card
// event as `gamble`; every screen that paints it reads it through normalizeGamble, because
// like everything in the state it arrived from whoever could POST it.

// How long the coin spins before it lands. Long enough to hold a room, short enough
// that a turn of three gambles is not a film.
export const GAMBLE_ROLL_MS = 2600;
// How long a phone keeps the result up after it lands.
export const GAMBLE_RESULT_MS = 3400;
// How long the board holds the result over the bar before the card slides to its victims.
export const GAMBLE_HOLD_MS = 1100;

const KINDS = new Set(["coin", "dice", "roulette", "wheel", "bottle", "duel"]);
const NUMBER_WORDS = ["ETTA", "TVÅA", "TREA", "FYRA", "FEMMA", "SEXA"];

// Lyckohjulet's five fields, in the order its spin numbers them (1–5).
export const WHEEL_SEGMENTS = ["3 klunkar", "−3 klunkar", "2 kort", "+2 mana", "Lyckokatten"];
const WHEEL_HEADLINES = ["PRICK!", "LÄKNING!", "2 KORT!", "+2 MANA!", "LYCKOKATTEN!"];

function sips(amount) {
  return `${amount} klunk${amount === 1 ? "" : "ar"}`;
}

function isWhole(value, min, max) {
  return Number.isInteger(value) && value >= min && value <= max;
}

function dice({value, sides, caption, win, detail}) {
  return {kind: "dice", caption, headline: `${NUMBER_WORDS[value - 1]}!`, detail, tone: win ? "win" : "lose", value, sides};
}

function roulette({caption, headline, detail, tone, candidates, picks}) {
  return {kind: "roulette", caption, headline, detail, tone, candidateIds: candidates.map((player) => player.id), pickIds: picks.map((player) => player.id)};
}

// One reader per chance card, mirroring that card's own play() in cards.js: the rolls
// arrive in the order the card made them. A roll that does not fit answers null, and
// the play simply gets the ordinary show.
const READERS = {
  myntkastet({rolls: [roll], self, target}) {
    if (!target || !isWhole(roll, 0, 1)) return null;
    const krona = roll === 0;
    return {
      kind: "coin",
      caption: `${self.name} singlar slant mot ${target.name} …`,
      headline: krona ? "KRONA!" : "KLAVE!",
      detail: krona ? `${target.name} tar ${sips(6)}` : `${self.name} tar ${sips(2)}`,
      tone: krona ? "win" : "lose",
      face: krona ? "krona" : "klave",
    };
  },
  tarningen({rolls: [roll], self, target}) {
    if (!target || !isWhole(roll, 1, 6)) return null;
    const backfire = roll <= 2;
    return dice({
      value: roll,
      sides: 6,
      caption: `${self.name} slår tärningen mot ${target.name} …`,
      win: !backfire,
      detail: backfire ? `${self.name} tar ${sips(3)}` : `${target.name} tar ${sips(roll)}`,
    });
  },
  kombucha({rolls: [roll], target}) {
    if (!target || !isWhole(roll, 1, 5)) return null;
    return dice({value: roll, sides: 5, caption: "Hur stark är kombuchan? …", win: roll >= 3, detail: `${target.name} tar ${sips(roll)}`});
  },
  shotroulette({rolls: [roll], self, enemies}) {
    const table = [self, ...enemies];
    if (!isWhole(roll, 0, table.length - 1)) return null;
    const victim = table[roll];
    const backfire = victim === self;
    return roulette({
      caption: "Vem får shotten? …",
      headline: backfire ? "BAKSLAG!" : `${victim.name.toUpperCase()}!`,
      detail: `${victim.name} tar ${sips(6)}`,
      tone: backfire ? "lose" : "win",
      candidates: table,
      picks: [victim],
    });
  },
  spillet({rolls, enemies}) {
    const pool = [...enemies];
    const picks = [];
    for (let splash = 0; splash < 2 && pool.length; splash += 1) {
      if (!isWhole(rolls[splash], 0, pool.length - 1)) return null;
      picks.push(...pool.splice(rolls[splash], 1));
    }
    if (!picks.length) return null;
    return roulette({
      caption: "Vem blir nedspillad? …",
      headline: `${picks.map((player) => player.name.toUpperCase()).join(" & ")}!`,
      detail: picks.length > 1 ? `${sips(2)} var` : `${picks[0].name} tar ${sips(2)}`,
      tone: "win",
      candidates: enemies,
      picks,
    });
  },
  "snurra-flaskan"({rolls: [roll], self, enemies}) {
    const table = [self, ...enemies];
    if (!isWhole(roll, 0, table.length - 1)) return null;
    const pick = table[roll];
    const home = pick === self;
    return {
      kind: "bottle",
      caption: "Flaskan snurrar …",
      headline: home ? "DIG SJÄLV!" : `${pick.name.toUpperCase()}!`,
      detail: home ? `${self.name} tar ${sips(2)} och drar 2 kort` : `${pick.name} tar ${sips(4)}`,
      tone: home ? "lose" : "win",
      candidateIds: table.map((player) => player.id),
      pickIds: [pick.id],
    };
  },
  tarningsduellen({rolls: [mine, theirs], self, target}) {
    if (!target || !isWhole(mine, 1, 6) || !isWhole(theirs, 1, 6)) return null;
    // A tie goes against the target, like the card says.
    const loser = mine < theirs ? 0 : 1;
    const amount = mine === theirs ? 2 : Math.abs(mine - theirs) * 2;
    return {
      kind: "duel",
      caption: `${self.name} utmanar ${target.name} …`,
      headline: mine === theirs ? "LIKA!" : `${mine} MOT ${theirs}!`,
      detail: `${loser ? target.name : self.name} tar ${sips(amount)}`,
      tone: loser ? "win" : "lose",
      values: [mine, theirs],
      names: [self.name, target.name],
      loser,
    };
  },
  lyckohjulet({rolls: [spin, pick], self, enemies}) {
    if (!isWhole(spin, 1, 5)) return null;
    let detail = [
      null,
      `${self.name} tar bort ${sips(3)}`,
      `${self.name} drar 2 kort`,
      `${self.name} får 2 mana`,
      `${self.name} får Lyckokatten`,
    ][spin - 1];
    if (spin === 1) {
      if (!enemies.length) detail = "Ingen att träffa";
      else if (!isWhole(pick, 0, enemies.length - 1)) return null;
      else detail = `${enemies[pick].name} tar ${sips(3)}`;
    }
    return {
      kind: "wheel",
      caption: `${self.name} snurrar lyckohjulet …`,
      headline: WHEEL_HEADLINES[spin - 1],
      detail,
      tone: "win",
      segments: WHEEL_SEGMENTS,
      index: spin - 1,
    };
  },
};

// The cards that get a moment of suspense. A test holds this against every card tagged
// as chance in the library, so a new gamble cannot quietly go without one.
export const GAMBLE_CARD_IDS = Object.keys(READERS);

// What a chance card's rolls meant, in the words and shapes the screens paint, or null.
// `enemies` must be the very list the card was handed: a roll is an index into it.
export function gambleOutcome(card, {rolls = [], self, target = null, enemies = []} = {}) {
  const read = READERS[card?.id];
  return read ? read({rolls, self, target, enemies}) : null;
}

function text(value, limit) {
  return String(value ?? "").slice(0, limit);
}

function whole(value, min, max, fallback) {
  const number = Math.trunc(Number(value));
  return Math.min(max, Math.max(min, Number.isFinite(number) && number !== 0 ? number : fallback));
}

function ids(value) {
  return Array.isArray(value) ? value.slice(0, 16).map((id) => text(id, 64)) : [];
}

// The gamble as a screen may paint it: a known kind, capped strings, numbers in range.
export function normalizeGamble(value) {
  if (!value || typeof value !== "object" || !KINDS.has(value.kind)) return null;
  const base = {
    kind: value.kind,
    caption: text(value.caption, 80),
    headline: text(value.headline, 80),
    detail: text(value.detail, 80),
    tone: value.tone === "lose" ? "lose" : "win",
    // A reveal for everyone at the table — a vote's raffle, which everyone took part in.
    ...(value.everyone === true ? {everyone: true} : {}),
  };
  if (value.kind === "coin") return {...base, face: value.face === "klave" ? "klave" : "krona"};
  if (value.kind === "dice") {
    const sides = whole(value.sides, 2, 6, 6);
    return {...base, sides, value: whole(value.value, 1, sides, 1)};
  }
  if (value.kind === "roulette" || value.kind === "bottle") {
    return {...base, candidateIds: ids(value.candidateIds), pickIds: ids(value.pickIds)};
  }
  if (value.kind === "duel") {
    const values = Array.isArray(value.values) ? value.values : [];
    const names = Array.isArray(value.names) ? value.names : [];
    return {
      ...base,
      values: [0, 1].map((side) => whole(values[side], 1, 6, 1)),
      names: [0, 1].map((side) => text(names[side], 24)),
      loser: value.loser === 0 ? 0 : 1,
    };
  }
  const segments = Array.isArray(value.segments) ? value.segments.slice(0, 8).map((segment) => text(segment, 24)) : [];
  return {...base, segments, index: Math.min(Math.max(0, segments.length - 1), Math.max(0, Math.trunc(Number(value.index)) || 0))};
}

// Where a spin stands `elapsed` ms in: it races at first and crawls at the end, like a
// real wheel, and stops on `landing` exactly at `duration`. Hops land at times that grow
// with the square of their number, which is what makes it slow down. `stride` other than
// 1 makes a die's faces tumble rather than count up.
export function spinIndex({elapsed, duration = GAMBLE_ROLL_MS, count, landing, laps = 3, stride = 1}) {
  const total = laps * count + landing;
  const progress = Math.min(1, Math.max(0, elapsed / duration));
  const hops = Math.floor(total * Math.sqrt(progress));
  return (((landing - (total - hops) * stride) % count) + count) % count;
}

// The roulette's spotlight over `count` candidates, landing on each pick in turn: the
// first is locked in halfway through a two-pick spin, and the spotlight runs on for the
// second. Once the roll is over every pick is locked and the spotlight is gone.
export function rouletteFrame(elapsed, count, landings) {
  if (!landings.length) return {spot: null, locked: []};
  if (elapsed >= GAMBLE_ROLL_MS) return {spot: null, locked: [...landings]};
  const leg = GAMBLE_ROLL_MS / landings.length;
  const current = Math.min(landings.length - 1, Math.floor(Math.max(0, elapsed) / leg));
  return {
    spot: spinIndex({elapsed: elapsed - current * leg, duration: leg, count, landing: landings[current]}),
    locked: landings.slice(0, current),
  };
}

// The face a tumbling die shows, from 1 to `sides`, landing on `value` after `duration`.
export function diceFace(elapsed, sides, value, {duration = GAMBLE_ROLL_MS, laps = 4} = {}) {
  return spinIndex({elapsed, duration, count: sides, landing: value - 1, laps, stride: sides - 1}) + 1;
}

// The thrower's die lands at three fifths of the roll, the target's only at the end:
// you see your own number and then wait for theirs.
const DUEL_FIRST = 0.6;

export function duelFaces(elapsed, values) {
  return values.map((value, side) => {
    const duration = side === 0 ? GAMBLE_ROLL_MS * DUEL_FIRST : GAMBLE_ROLL_MS;
    return {face: diceFace(elapsed, 6, value, {duration, laps: side === 0 ? 3 : 5}), rolling: elapsed < duration};
  });
}

// A die's face as a 3×3 grid of pips, laid out the way real dice are.
const PIPS = {1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8]};

export function dicePips(value) {
  const lit = PIPS[value] || PIPS[1];
  return Array.from({length: 9}, (_, cell) => lit.includes(cell));
}

// How far the wheel turns so the chosen field's middle stops under the pointer at the top.
export function wheelAngle(index, count, laps = 5) {
  return laps * 360 + 360 - ((index + 0.5) * 360) / count;
}

// How far the coin turns: whole turns for krona, half a turn more for klave.
export function coinAngle(face, turns = 7) {
  return turns * 360 + (face === "klave" ? 180 : 0);
}

// Fast first, slow last: how far along a toss or a spin is at `progress` of its time.
export function easeOut(progress) {
  const clamped = Math.min(1, Math.max(0, progress));
  return 1 - (1 - clamped) ** 3;
}

// The coin `elapsed` ms into its toss: how far it has turned, and how high it is (0 on
// the bar, 1 at the top of the arc).
export function coinFrame(elapsed, face) {
  const progress = Math.min(1, Math.max(0, elapsed / GAMBLE_ROLL_MS));
  return {angle: coinAngle(face) * easeOut(progress), lift: Math.round(Math.sin(Math.PI * progress) * 1000) / 1000};
}

// How far the wheel has turned `elapsed` ms into its spin.
export function wheelFrame(elapsed, index, count) {
  return wheelAngle(index, count) * easeOut(elapsed / GAMBLE_ROLL_MS);
}

// Which field sits under the pointer at the top when the wheel has turned `angle`.
export function wheelPointer(angle, count) {
  const under = (((360 - angle) % 360) + 360) % 360;
  return Math.floor(under / (360 / count)) % count;
}

// Where the bottle ends up pointing, as a CSS rotation (0° straight up, clockwise),
// after `laps` whole turns: at a point `dx`, `dy` from the middle of the screen — the
// victim's seat on the TV —
export function bottleAim(dx, dy, laps = 5) {
  const heading = (Math.atan2(dx, -dy) * 180) / Math.PI;
  return laps * 360 + ((heading % 360) + 360) % 360;
}

// — or at the `index`th of `count` names laid round a ring clockwise from the top, which
// is how a phone draws the table.
export function bottleRing(index, count, laps = 5) {
  return laps * 360 + (index * 360) / count;
}

export function bottleSpin(elapsed, finalAngle) {
  return finalAngle * easeOut(elapsed / GAMBLE_ROLL_MS);
}

export function gamblePhase(elapsed) {
  if (elapsed < GAMBLE_ROLL_MS) return "rolling";
  return elapsed < GAMBLE_ROLL_MS + GAMBLE_RESULT_MS ? "result" : "done";
}
